import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AuthApi } from 'src/app/shared/apis/auth.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from 'src/app/shared/components/cs-icon-button/cs-icon-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent, ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { VerifyAction } from 'src/app/shared/interfaces/auth.interface';
import { Country } from 'src/app/shared/interfaces/country.interface';
import { CacheService } from 'src/app/shared/service/cache.service';
import { MatConnectedDialogRef } from 'src/app/shared/service/connected-dialog';
import { ToastService } from 'src/app/shared/service/toast.service';

@Component({
  selector: 'kyc-dialog',
  templateUrl: './kyc-dialog.component.html',
  styleUrls: ['./kyc-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CSButtonComponent,
    CSIconButtonComponent,
    ReactiveFormsModule,
    CustomizeFormModule,
    CSSelectComponent,
    CSInputComponent,
  ],
})
export class KycDialogComponent implements OnInit {
  public formSubmitted = false;
  public countryOptions: ICSOption<string>[] = [];
  public restrictedCountryKeys: string[] = [];
  public countryControl = new FormControl<string | null>(null, Validators.required);
  public termsControl = new FormControl<boolean>(true, Validators.requiredTrue);
  public isMobileDevice = false;
  public nativeDateValue = '';
  @ViewChild('nativeDobInput') private nativeDobInput?: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private cacheService: CacheService,
    private appService: AppService,
    private authApi: AuthApi,
    private toast: ToastService,
    private dialogRef?: MatConnectedDialogRef<KycDialogComponent>,
  ) {}

  @Input() title: string = 'ACCOUNT DETAILS';

  private _requireTerms: boolean = false;
  @Input()
  get requireTerms(): boolean {
    return this._requireTerms;
  }
  set requireTerms(value: boolean) {
    this._requireTerms = value;
    if (value) {
      this.termsControl.setValue(false);
    }
  }

  private _requireEmail = true;
  @Input()
  get requireEmail(): boolean {
    return this._requireEmail;
  }
  set requireEmail(value: boolean) {
    this._requireEmail = value;
    const control = this.form?.get('email');
    if (!control) return;
    if (value) {
      control.setValidators([Validators.required, Validators.email]);
      control.enable({ emitEvent: false });
    } else {
      control.clearValidators();
      control.disable({ emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private _prefillEmail: string = '';
  @Input()
  get prefillEmail(): string {
    return this._prefillEmail;
  }
  set prefillEmail(value: string) {
    this._prefillEmail = value || '';
    const control = this.form?.get('email');
    if (!control) return;
    control.setValue(this._prefillEmail, { emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  @Input() loading: boolean = false;

  @Output() submitted = new EventEmitter<Record<string, unknown>>();
  @Output() closed = new EventEmitter<void>();

  @HostListener('document:keydown.enter', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    const submitDisabled = this.loading || (this.formSubmitted && this.form.invalid);
    if (event.key === 'Enter' && !submitDisabled) {
      this.submit();
    }
  }

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', Validators.required],
    surname: ['', Validators.required],
    dateOfBirth: ['', [Validators.required, Validators.pattern(/^\d{2}\/\d{2}\/\d{4}$/), this.minAgeValidator(18)]],
    street: ['', Validators.required],
    city: ['', Validators.required],
    postcode: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(10)]],
    country: this.countryControl,
    agreeTerms: this.termsControl,
  });

  ngOnInit(): void {
    this.isMobileDevice = this.detectMobileDevice();
    this.initCountries();
    this.syncNativeDateValueFromForm();
  }

  public async submit(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.formSubmitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.form.value;

    const email = payload?.email || '';
    if (email) {
      this.loading = true;
      try {
        const verifyRes = await firstValueFrom(
          this.authApi.verifyEmailBeforeOtp({
            email,
            otpType: 'Register' as VerifyAction,
          }),
        );

        if (!verifyRes?.success) {
          this.toast.show({
            message: verifyRes?.message || 'This email is already registered. Please sign in instead.',
            type: 'fail',
          });
          return;
        }
      } catch (error) {
        console.error('KycDialog verifyEmailBeforeOtp error', error);
        return;
      } finally {
        this.loading = false;
      }
    }

    if (this.submitted.observers.length) {
      this.submitted.emit(payload);
    } else {
      this.dialogRef?.close(payload);
    }
  }

  public formatDateOfBirthInput = (value: unknown): string => {
    const raw = typeof value === 'string' ? value : String(value ?? '');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (!digits) {
      this.nativeDateValue = '';
      return '';
    }

    let formatted = digits;
    if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    this.nativeDateValue = this.toNativeIsoOrEmpty(formatted);
    return formatted;
  };

  public onDateOfBirthFocus(): void {
    if (!this.isMobileDevice) {
      return;
    }
    this.syncNativeDateValueFromForm();
    this.tryOpenNativeDatePicker();
  }

  public onNativeDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      this.setDateOfBirthValue('');
      return;
    }

    const parsed = this.parseIsoDate(value);
    if (!parsed) {
      return;
    }

    this.nativeDateValue = this.formatAsIsoDate(parsed);
    this.setDateOfBirthValue(this.formatAsDayMonthYear(parsed));
  }

  public selectCountry(iso: string | null): void {
    this.countryControl.setValue(iso);
    this.countryControl.markAsTouched();
  }

  public jumpToAgree(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const url = this.appService.termsConditionsLink;
    if (!url) return;
    window.open(`${window.location.origin}/${this.appService.languageCode}${url}`, '_blank');
  }

  public jumpToPolicy(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const url = this.appService.privacyPolicyLink;
    url && window.open(`${window.location.origin}/${this.appService.languageCode}${url}`, '_blank');
  }

  public getDateOfBirthError(): string {
    const control = this.form.get('dateOfBirth');
    if (!control || !(control.dirty || control.touched || this.formSubmitted)) return '';
    const errors = control.errors || {};
    if (errors['minAge']) return `Minimum age requirement is ${errors['minAge'].requiredAge} years old`;
    if (errors['invalidDate']) {
      return 'Please enter a valid date of birth (DD/MM/YYYY)';
    }
    if (errors['required'] || errors['pattern']) {
      return 'Please provide your date of birth (DD/MM/YYYY)';
    }
    return '';
  }

  public close() {
    if (this.closed.observers.length) {
      this.closed.emit();
    } else {
      this.dialogRef?.close();
    }
  }

  public hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched || this.formSubmitted);
  }

  public countryHasError(): boolean {
    return (
      this.countryControl.invalid && (this.countryControl.dirty || this.countryControl.touched || this.formSubmitted)
    );
  }

  public termsHasError(): boolean {
    return (
      this.requireTerms &&
      this.termsControl.invalid &&
      (this.termsControl.dirty || this.termsControl.touched || this.formSubmitted)
    );
  }

  private initCountries(): void {
    const countries = this.cacheService.countries || [];

    const restrictedFromKycRaw = this.appService.tenantConfig$.value?.config?.restrictedCountriesFromKYC;
    const restrictedFromKyc = JSON.parse((restrictedFromKycRaw as string) || '[]') as unknown[];
    this.restrictedCountryKeys = restrictedFromKyc.filter(
      (item): item is string => typeof item === 'string' && item.trim().length > 0,
    );
    const restrictedSet = new Set(this.restrictedCountryKeys.map(key => key.toLowerCase()));

    const availableCountries = countries.filter(country => !restrictedSet.has((country.code || '').toLowerCase()));
    this.countryOptions = availableCountries
      .map(country => this.mapCountryToOption(country))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

    const defaultIso = this.appService.currentCountry$.value?.iso || this.appService.countryCode;
    const matched = availableCountries.find(country => country.iso.toUpperCase() === (defaultIso || '').toUpperCase());
    if (matched) {
      this.countryControl.setValue(matched.iso);
    }
  }

  private mapCountryToOption(country: Country): ICSOption<string> {
    return {
      label: country.name,
      value: country.iso,
      img: this.buildFlagPath(country.code),
      data: country,
    };
  }

  private buildFlagPath(code: string): string {
    const sanitized = `country-${code}`
      .replace(/\(/g, '')
      .replace(/\)/g, '')
      .replace(/&/g, '_and_')
      .replace(/ /g, '_')
      .replace(/,/g, '')
      .replace(/\./g, '')
      .replace(/'/g, '')
      .replace(/\//g, '_');

    return `assets/images/media/flags/${sanitized}.svg`;
  }

  private syncNativeDateValueFromForm(): void {
    const value = this.form.get('dateOfBirth')?.value;
    if (typeof value !== 'string' || !value) {
      this.nativeDateValue = '';
      return;
    }

    this.nativeDateValue = this.toNativeIsoOrEmpty(value);
  }

  private parseDate(value: string): Date | null {
    const [day, month, year] = value.split('/').map(part => Number(part));
    if (!day || !month || !year) {
      return null;
    }

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  private parseIsoDate(value: string): Date | null {
    const parts = value.split('-').map(part => Number(part));
    if (parts.length !== 3 || parts.some(part => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    const parsed = new Date(year, month - 1, day);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
      return null;
    }

    return parsed;
  }

  private toNativeIsoOrEmpty(value: string): string {
    const parsed = this.parseDate(value);
    return parsed ? this.formatAsIsoDate(parsed) : '';
  }

  private formatAsIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatAsDayMonthYear(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private setDateOfBirthValue(value: string): void {
    const control = this.form.get('dateOfBirth');
    control?.setValue(value);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  private detectMobileDevice(): boolean {
    if (typeof document !== 'undefined' && document.body.classList.contains('is-mobile')) {
      return true;
    }

    const userAgent = navigator.userAgent || '';
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent);
  }

  private tryOpenNativeDatePicker(): void {
    const picker = this.nativeDobInput?.nativeElement;
    if (!picker) {
      return;
    }

    picker.value = this.nativeDateValue;
    const pickerWithShow = picker as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerWithShow.showPicker !== 'function') {
      return;
    }

    try {
      pickerWithShow.showPicker();
    } catch {
      // If showPicker is blocked by browser policy, keep manual typing path untouched.
    }
  }

  private minAgeValidator(minAge: number): ValidatorFn {
    return control => {
      if (!control.value) return null;
      const parsed = this.parseDate(control.value);
      if (!parsed) return { invalidDate: true };

      const today = new Date();
      const age = today.getFullYear() - parsed.getFullYear();
      const hasHadBirthdayThisYear =
        today.getMonth() > parsed.getMonth() ||
        (today.getMonth() === parsed.getMonth() && today.getDate() >= parsed.getDate());
      const effectiveAge = hasHadBirthdayThisYear ? age : age - 1;

      return effectiveAge < minAge ? { minAge: { requiredAge: minAge } } : null;
    };
  }
}
